import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * OOS Alternative Products Component
 * Manages visibility of alternative product recommendations based on variant availability.
 */
class OosAlternativeProducts extends HTMLElement {
  /** @type {HTMLElement | null} */
  #blockContainer = null;

  /** @type {HTMLElement | null} */
  #blockWrapper = null;

  /** @type {string | null} */
  #productId = null;

  /** @type {((event: VariantUpdateEvent) => void) | null} */
  #variantUpdateHandler = null;

  /** @type {Element | null} */
  #closestSection = null;

  /**
   * Map of variant ID -> resolved OOS boolean for this region.
   * Server (Liquid) resolves the full priority chain (variant DC -> product DC ->
   * OOTB variant.available) via the `dc-variant-in-stock` snippet, so the JS
   * lookup is just a flag read — never re-implements the resolution logic.
   * `true` => OOS in this region. `false` => in-stock in this region.
   * Absent map (null property) means non-AU/NZ market — fall back to variant.available.
   * @type {Record<string, boolean> | null}
   */
  #variantStockMap = null;

  connectedCallback() {
    this.#blockContainer = this.querySelector('.oos-alternative-products');
    if (!this.#blockContainer) return;

    this.#blockWrapper = this.closest('.oos-alternative-products-block');
    this.#productId = this.dataset.productId || null;
    this.#closestSection = this.closest('.shopify-section, dialog');
    this.#loadVariantStockMap();
    
    if (!this.#closestSection) return;

    this.#variantUpdateHandler = this.#handleVariantUpdate.bind(this);
    this.#closestSection.addEventListener(ThemeEvents.variantUpdate, /** @type {EventListener} */ (this.#variantUpdateHandler));
    
    // Initialize visibility after DOM and variant data are ready
    setTimeout(() => this.#initializeOnPageLoad(), 100);
  }

  /**
   * Loads per-variant region DC stock map injected by Liquid.
   */
  #loadVariantStockMap() {
    const stockScript = this.querySelector('script[data-dc-variant-stock]');
    const text = stockScript?.textContent?.trim();
    if (!text) return;
    try {
      this.#variantStockMap = JSON.parse(text);
    } catch {
      this.#variantStockMap = null;
    }
  }
  
  /**
   * Initializes visibility on page load based on URL variant parameter
   */
  #initializeOnPageLoad() {
    // Sync block wrapper visibility with Liquid's initial state
    const initialHidden = this.dataset.initialHidden === 'true';
    if (this.#blockWrapper) {
      this.#blockWrapper.classList.toggle('hidden', initialHidden || this.hidden);
    }
    
    // Check URL variant parameter
    const urlVariantId = new URLSearchParams(window.location.search).get('variant');
    if (!urlVariantId) return;
    
    const variant = this.#getVariantData(urlVariantId);
    if (variant) {
      this.#updateVisibility(variant);
    }
  }
  
  /**
   * Handles variant update events
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (!event?.detail) return;

    const eventProductId = event.detail.data?.productId;
    if (eventProductId && this.#productId && eventProductId !== this.#productId) {
      return;
    }

    if (event.detail.data?.newProduct) {
      this.#productId = event.detail.data.newProduct.id;
    }

    this.#updateVisibility(event.detail.resource);
  };

  /**
   * Gets variant data by variant ID
   * @param {string|number} variantId
   * @returns {Object|null}
   */
  #getVariantData(variantId) {
    const win = /** @type {Window & {product?: {variants?: any[]}}} */ (window);
    if (win.product?.variants) {
      const variant = win.product.variants.find((v) => 
        v.id?.toString() === variantId.toString()
      );
      if (variant) return variant;
    }
    
    const variantPicker = this.closest('variant-picker') || 
                          document.querySelector(`variant-picker[data-product-id="${this.#productId}"]`);
    
    if (variantPicker) {
      const option = variantPicker.querySelector(`[data-variant-id="${variantId}"]`);
      if (option instanceof HTMLElement) {
        return {
          id: variantId,
          available: option.dataset.optionAvailable !== 'false'
        };
      }
    }
    
    return null;
  }

  /**
   * Determines if a variant should be treated as out-of-stock for this region.
   * AU/NZ: server-resolved boolean from the variant stock map (covers the full
   * priority chain incl. product-level DC fallback). Non-AU/NZ market or variant
   * not found in map: fall back to variant.available.
   * @param {Object | null} variant
   * @returns {boolean}
   */
  #isVariantOutOfStock(variant) {
    if (!variant) return true;

    const variantId = variant.id != null ? variant.id.toString() : null;
    const resolved = variantId != null && this.#variantStockMap
      ? this.#variantStockMap[variantId]
      : undefined;

    if (typeof resolved === 'boolean') {
      return resolved;
    }

    return 'available' in variant &&
      (variant.available === false || variant.available === 'false');
  }

  /**
   * Updates block visibility based on variant availability
   * @param {Object | null} variant
   */
  #updateVisibility(variant) {
    if (!this.#blockContainer) return;

    const isOutOfStock = this.#isVariantOutOfStock(variant);
    const hasAlternativeProducts = 
      this.#blockContainer.querySelector('.oos-alternative-products__carousel-wrapper, .oos-alternative-products__placeholder') !== null;

    const shouldShow = isOutOfStock && hasAlternativeProducts;
    
    this.#blockContainer.hidden = !shouldShow;
    this.hidden = !shouldShow;
    
    if (this.#blockWrapper) {
      this.#blockWrapper.classList.toggle('hidden', !shouldShow);
    }
  }

  disconnectedCallback() {
    if (this.#closestSection && this.#variantUpdateHandler) {
      this.#closestSection.removeEventListener(ThemeEvents.variantUpdate, this.#variantUpdateHandler);
    }
    
    this.#variantUpdateHandler = null;
    this.#closestSection = null;
    this.#blockContainer = null;
    this.#blockWrapper = null;
    this.#variantStockMap = null;
  }
}

if (!customElements.get('oos-alternative-products')) {
  customElements.define('oos-alternative-products', OosAlternativeProducts);
}
